<template>
    <div class="report-container">
        <headerPage />

        <!-- 顶部信息 -->
        <div class="report-header">
            <div class="title-wrapper">
                <el-button @click="goBack()" type="text" icon="el-icon-arrow-left" class="return-button">返回</el-button>
                <span class="divider">|</span>
                <h2 class="report-title">{{ examTitle }}</h2>
            </div>


            <!-- 统计表格 -->
            <div class="report-summary">
                <el-table :data="tableData" border style="width: 100%">
                    <el-table-column label="题型" prop="type" align="center" />
                    <el-table-column label="总共" align="center">
                        <template #default="{ row }">
                            <span>{{ row.total }}道</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="答对" align="center">
                        <template #default="{ row }">
                            <span style="color: #67c23a">{{ row.right }}道</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="答错" align="center">
                        <template #default="{ row }">
                            <span style="color: #f56c6c">{{ row.wrong }}道</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="未答" align="center">
                        <template #default="{ row }">
                            <span style="color: #909399">{{ row.unanswered }}道</span>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </div>

        <!-- 试题内容 -->
        <div class="quiz-content">
            <div class="question-area">
                <el-card v-for="(question, index) in questionList" :key="index" class="question-card">
                    <div class="question-title">
                        <span>
                            {{ index + 1 }}. {{ question.title }}
                            <span v-if="question.questionType === 2">(多选)</span>
                        </span>
                    </div>

                    <!-- 选项区域 -->
                    <div class="question-options">
                        <el-radio-group v-if="question.questionType === 1" value="question.userAnswer">
                            <el-radio v-for="(option) in question.options" :key="option.value" :label="option.value"
                                class="option-item" :class="getAnswerClass(question, option)">
                                {{ option.value }}. {{ option.label }}
                            </el-radio>
                        </el-radio-group>

                        <el-checkbox-group v-else-if="question.questionType === 2" value="question.userAnswer">
                            <el-checkbox v-for="(option) in question.options" :key="option.value" :label="option.value"
                                class="option-item" :class="getAnswerClass(question, option)">
                                {{ option.value }}. {{ option.label }}
                            </el-checkbox>
                        </el-checkbox-group>

                        <el-radio-group v-else-if="question.questionType === 3" value="question.userAnswer">
                            <el-radio v-for="(option) in question.options" :key="option.value" :label="option.value"
                                class="option-item" :class="getAnswerClass(question, option)">
                                {{ option.value }}. {{ option.label }}
                            </el-radio>
                        </el-radio-group>
                    </div>

                    <!-- 解析区域 -->
                    <div class="question-analysis">
                        <el-divider></el-divider>
                        <div class="answer-status">
                            <p><strong>回答情况：</strong> 正确答案：</p>
                            <p style="color: #67c23a;">{{ question.correct }}</p>
                            <p v-if="question.doRight === 0">回答正确</p>
                            <p v-if="question.doRight === 1">，你的答案：</p>
                            <p v-if="question.doRight === 1" style="color: #f56c6c;">
                                {{ question.userAnswer.join('') || "未作答" }}</p>
                        </div>
                        <div class="explanation"
                            style="background-color: rgba(224, 247, 250, .4); padding: 10px; border-radius: 5px;">
                            <p><strong>解析：{{ question.analysis }}</strong></p>
                        </div>
                    </div>
                </el-card>
            </div>

            <!-- 右侧答题卡 -->
            <div class="question-card-panel">
                <div class="card-header">
                    <span class="card-title">答题卡</span>
                    <el-button type="text" size="mini" @click="toggleCardCollapse">
                        {{ isCardCollapsed ? "展开答题卡" : "收起答题卡" }}
                    </el-button>
                </div>

                <div v-show="!isCardCollapsed" class="question-nav">
                    <el-button v-for="(q, idx) in questionList" :key="idx" @click="scrollToQuestion(idx)"
                        :class="getCardClass(q)" type="default" size="mini">
                        {{ idx + 1 }}
                    </el-button>
                </div>
            </div>
        </div>

        <bottomPage />
    </div>
</template>

<script>
import headerPage from "@/components/header/header";
import bottomPage from "@/components/bottom/bottom";

import * as PaperApi from "@/api/paper";

export default {
    components: {
        headerPage,
        bottomPage,
    },
    data() {
        return {
            examTitle: "",
            tableData: [],
            questionList: [],
            isCardCollapsed: false,
            loadingInstance: null,
        };
    },
    created() {
        this.loadingInstance = this.$loading({
            lock: true,
            text: "正在加载试题，请稍候...",
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.4)",
        });
        this.loadReport();
    },
    methods: {
        toggleCardCollapse() {
            this.isCardCollapsed = !this.isCardCollapsed;
        },
        scrollToQuestion(index) {
            const element = document.querySelectorAll(".question-card")[index];
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        },
        async loadReport() {
            try {
                const res = await PaperApi.getPaperReport(this.$route.params.reportId);
                if (res.code !== 0 || !res.data) return;

                const data = res.data;
                this.examTitle = data.paperDetail.paperName;

                const formatQuestion = (q) => {
                    const base = {
                        title: q.title,
                        questionId: q.questionId,
                        questionType: q.questionType,
                        doRight: q.doRight,
                        correct: q.correct,
                        userAnswer: q.userAnswer.split(''),
                        analysis: q.analysis,
                    };
                    const options = [];
                    if (q.questionType === 1) {
                        ["A", "B", "C", "D"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    } else if (q.questionType === 2) {
                        ["A", "B", "C", "D", "E"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    } else if (q.questionType === 3) {
                        ["A", "B"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    }
                    return base;
                };

                const question = data.questionList.map((q) =>
                    formatQuestion(q)
                );

                this.questionList = [...question];


                var statistics = {
                    type: "题目列表",
                    total: 0,
                    right: 0,
                    wrong: 0,
                    unanswered: 0,
                };
                this.questionList.forEach((question) => {
                    statistics.total += 1;

                    if (question.doRight === 0) {
                        statistics.right += 1;
                    } else if (question.userAnswer.length === 0) {
                        statistics.unanswered += 1;
                    } else if (question.userAnswer.join('') !== question.correct) {
                        statistics.wrong += 1;
                    }
                });
                this.tableData.push(statistics);

            } finally {
                if (this.loadingInstance) {
                    this.loadingInstance.close();
                }
            }
        },
        getAnswerStyle(question, option) {
            // 单选题
            if (question.questionType === 1) {
                if (question.userAnswer.includes(option.value)) {
                    // 用户选择的选项
                    return option.value === question.correct
                        ? { color: 'green', fontWeight: 'bold' }
                        : { color: 'red', fontWeight: 'bold' };
                } else if (option.value === question.correct) {
                    // 正确答案，用户没有选择
                    return { color: 'green', fontWeight: 'bold' };
                }
            }

            // 多选题
            if (question.questionType === 2) {
                if (question.userAnswer.includes(option.value)) {
                    // 用户选择的选项
                    return question.correct.includes(option.value)
                        ? { color: 'green', fontWeight: 'bold' }  // 正确选项，显示绿色
                        : { color: 'red', fontWeight: 'bold' };   // 错误选项，显示红色
                } else if (question.correct.includes(option.value)) {
                    // 正确答案，但用户没有选择
                    return { color: 'green', fontWeight: 'bold' }; // 正确答案，显示绿色
                }
            }

            // 判断题
            if (question.questionType === 3) {
                if (question.userAnswer.includes(option.value)) {
                    return option.value === question.correct
                        ? { color: 'green', fontWeight: 'bold' }
                        : { color: 'red', fontWeight: 'bold' };
                } else if (option.value === question.correct) {
                    return { color: 'green', fontWeight: 'bold' }; // 正确答案，显示绿色
                }
            }

            return {};  // 如果没有选择，则返回空样式
        },
        getAnswerClass(question, option) {
            const isSelected = question.userAnswer.includes(option.value);
            const isCorrect = question.correct.includes(option.value);

            if (isSelected) {
                if (question.questionType === 1 || question.questionType === 3) {
                    return option.value === question.correct ? 'option-correct' : 'option-wrong';
                } else if (question.questionType === 2) {
                    return isCorrect ? 'option-correct' : 'option-wrong';
                }
            } else if (isCorrect) {
                return 'option-correct';
            }

            return '';
        },
        getCardClass(question) {
            if (question.doRight === 0) {
                return 'answered correct';
            } else if (question.userAnswer.length === 0) {
                return 'answered unanswered';
            }
            return 'answered wrong';
        },
        goBack() { 
            this.$router.push("/paperRecord");
        },
    },
};
</script>

<style>
/* 正确选项绿色 */
.option-correct .el-radio__inner,
.option-correct .el-checkbox__inner {
    border-color: #67c23a !important;
    background-color: #67c23a !important;
}

.option-correct .el-radio__inner::after,
.option-correct .el-checkbox__inner::after {
    border-color: white;
}

/* 错误选项红色 */
.option-wrong .el-radio__inner,
.option-wrong .el-checkbox__inner {
    border-color: #f56c6c !important;
    background-color: #f56c6c !important;
}

.option-wrong .el-radio__inner::after,
.option-wrong .el-checkbox__inner::after {
    border-color: white;
}
</style>
<style scoped>
.report-container {
    background-color: #f4f4f4;
    padding: 20px;
    min-height: 100vh;
}

.report-header {
    margin: 20px auto;
    padding: 20px 30px;
    background: #fff;
    border-radius: 8px;
    max-width: 1200px;
}

.title-wrapper {
    display: flex;
    align-items: center;
}

.divider {
    color: #ccc;
    font-size: 16px;
    margin: 6px 10px 0px 5px;
}

/* 调整返回按钮和标题之间的间距 */
.return-button {
    color: #333 !important;
    margin-top: 10px;
    margin-right: 10px;
}

.report-title {
    font-size: 22px;
    font-weight: bold;
    color: #333;
    margin-bottom: 10px;
}

.report-summary {
    margin: 20px auto;
    max-width: 1200px;
}

.quiz-content {
    display: flex;
    max-width: 1200px;
    margin: 20px auto;
    gap: 20px;
}

.question-area {
    flex: 2;
}

.question-card {
    background-color: #f9f9f9;
    margin-bottom: 15px;
    padding: 15px;
    border-radius: 5px;
}

.question-title {
    font-weight: bold;
    margin-bottom: 10px;
}

.question-options {
    margin-top: 10px;
}

.option-item {
    display: block;
    margin: 6px 0;
}

.question-analysis {
    margin-top: 10px;
}

.answer-status {
    margin-bottom: 10px;
}

.answer-status p {
    display: inline;
    white-space: nowrap;
    margin-right: 5px;
    /* 可以根据需要调整间距 */
}

.explanation {
    background-color: #ffffff;
    padding: 10px;
    border-radius: 5px;
    font-size: 14px;
}

.question-card-panel {
    min-width: 220px;
    flex: 0;
    background: #fff;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #eee;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 20px;
    height: fit-content;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.card-title {
    font-weight: bold;
}

.question-nav {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    max-height: 230px;
    overflow-y: auto;
    padding: 5px;
}

.question-nav button {
    width: 36px;
    height: 36px;
    font-size: 13px;
    text-align: center;
    justify-content: center;
    align-items: center;
    display: flex;
    padding: 0;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    background-color: #fff;
    transition: 0.2s;
    margin: 0;
}

.answered.correct {
    background-color: #67c23a !important;
    color: white;
}

.answered.wrong {
    background-color: #f56c6c !important;
    color: white;
}

.answered.unanswered {
    background-color: #e4e7ed !important;
}

.countdown {
    font-size: 14px;
    color: #f56c6c;
    font-weight: bold;
    text-align: center;
    margin-bottom: 10px;
    padding: 6px;
    border-radius: 4px;
    background-color: #fff7f7;
    border: 1px solid #fbc4c4;
}

.time-text {
    font-size: 16px;
}
</style>
